import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Download, FileImage, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";

/**
 * Captures the react-flow canvas inside `getNode()` and exports as PNG or PDF.
 * Uses `fitView` first to ensure the whole tree is captured.
 */
export default function ExportMenu({ reactFlowInstance, getNode, companyName = "organogram" }) {
  const [busy, setBusy] = useState(false);

  const prepareNode = async () => {
    if (reactFlowInstance) {
      try {
        reactFlowInstance.fitView({ padding: 0.1, duration: 0 });
      } catch (_) {}
      await new Promise((r) => setTimeout(r, 400));
    }
    return getNode?.();
  };

  const capture = async () => {
    const node = await prepareNode();
    if (!node) throw new Error("Canvas not found");
    // Hide controls/minimap from the export
    const hide = node.querySelectorAll(".react-flow__controls,.react-flow__minimap,.react-flow__panel");
    hide.forEach((el) => (el.style.visibility = "hidden"));
    try {
      const dataUrl = await toPng(node, {
        backgroundColor: "#F4F4F2",
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: true,
      });
      return dataUrl;
    } finally {
      hide.forEach((el) => (el.style.visibility = ""));
    }
  };

  const baseName = () => `${(companyName || "organogram").toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}`;

  const exportPng = async () => {
    setBusy(true);
    try {
      const dataUrl = await capture();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${baseName()}.png`;
      a.click();
      toast.success("PNG downloaded");
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    }
    setBusy(false);
  };

  const exportPdf = async () => {
    setBusy(true);
    try {
      const dataUrl = await capture();
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => (img.onload = res));
      const orientation = img.width >= img.height ? "landscape" : "portrait";
      const pdf = new jsPDF({ orientation, unit: "px", format: [img.width, img.height], compress: true });
      pdf.addImage(dataUrl, "PNG", 0, 0, img.width, img.height);
      pdf.save(`${baseName()}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    }
    setBusy(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={busy}
          className="rounded-none border-neutral-300"
          data-testid="export-menu-button"
        >
          {busy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Download size={14} className="mr-2"/>}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="rounded-none">
        <DropdownMenuItem onClick={exportPng} className="rounded-none" data-testid="export-png-item">
          <FileImage size={14} className="mr-2"/> Export as PNG
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportPdf} className="rounded-none" data-testid="export-pdf-item">
          <FileText size={14} className="mr-2"/> Export as PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
